import React, { useState } from 'react';
import {
    X, Search, Bell, Zap, BarChart3, Gift,
    CheckCircle, CalendarCheck, HelpCircle, ImageIcon,
} from 'lucide-react';

// =====================================================================
// AVISO: "Conheça a tela de início" — pop-up (tutorial da Home)
// =====================================================================
// É o CONTEÚDO do 1º aviso do Sino (chave 'home-tour-2026-06'). Explica, parte
// por parte, para que serve cada área da Home e o que o usuário pode fazer nela,
// com um PRINT da área acima de cada explicação e uma lista de ações possíveis.
//
// Por que o conteúdo vive aqui (e não no banco): o tutorial é fixo e "desenha"
// as seções da Home — é UI, não texto editável. Quando surgir a tela de Admin
// (fase 2), os avisos do dia a dia serão texto; este segue como card especial.
//
// IMAGENS: cada seção aponta para um print em /public/avisos/home-tour/ (campo
// `imagem`). Enquanto o arquivo não existe, aparece uma moldura "imagem em
// breve" (ver componente Imagem) — nunca um ícone quebrado. Para trocar/incluir
// um print, basta soltar o PNG com o nome esperado na pasta (ver README de lá).
//
// Props:
//   - onClose : fecha o pop-up (chamado no X, no fundo e no botão "Entendi").
//
// Observação: este componente é SÓ visual. Marcar o aviso como lido é
// responsabilidade de quem abre o pop-up (o SinoAvisos dispara o POST /ler).
// ---------------------------------------------------------------------

const COR = {
    vermelhoEscuro: '#990605',
    amarelo: '#FDBB20',
    texto: '#1a1a1a',
    cinzaTexto: '#555',
    cinzaSuave: '#7a7a7a',
    fundoIcone: '#FFF2F2',
    borda: 'rgba(199,194,194,0.45)',
    molduraFundo: '#f4f3f3',
};
const FONTE = "'Raleway', sans-serif";

// Pasta pública dos prints do tutorial (o usuário solta os PNGs aqui).
const BASE_IMG = '/avisos/home-tour';

// Cada seção do tutorial: visual + ícone + título + explicação + ações possíveis.
//   imagem   : caminho do print (em /public). Se faltar o ARQUIVO, vira moldura
//              "em breve". Seção SEM o campo `imagem` mostra uma ILUSTRAÇÃO de
//              ícone no lugar (usado onde um print não fica bom — Sino, Busca).
//   icones   : (opcional) ícones extras da ilustração, quando não há print.
//   texto    : parágrafo explicativo (o que é / para que serve).
//   acoes    : lista curta "O que você pode fazer" (marcadores).
//   destaque : pinta o ícone de amarelo (usado no próprio Sino, a novidade).
const SECOES = [
    {
        icone: Zap,
        titulo: 'Acesso Rápido',
        imagem: `${BASE_IMG}/acesso-rapido.png`,
        texto: 'É a fileira de atalhos no topo do conteúdo. Eles levam direto às áreas que você mais usa, sem precisar procurar no menu lateral. Os atalhos já liberados são o de Colaboradores (consulta de quem trabalha na rede) e o de Indicadores (painéis de auditoria, treinamento e financeiro). Os atalhos com cadeado e a marca "Em breve" ainda estão sendo construídos e vão sendo liberados aos poucos.',
        acoes: [
            'Abrir a Consulta de Colaboradores num clique',
            'Abrir os Indicadores num clique',
            'Identificar o que ainda está por vir (atalhos com cadeado)',
        ],
    },
    {
        icone: BarChart3,
        titulo: 'Indicadores da Loja',
        imagem: `${BASE_IMG}/indicadores.png`,
        texto: 'Um resumo rápido dos números da sua loja no mês: admissões, desligamentos, atestados e Primeiros 15 dias. Serve para você bater o olho na situação atual sem abrir relatório nenhum. Cada número vem com uma comparação em relação ao mês anterior, mostrada por uma setinha colorida.',
        acoes: [
            'Alternar entre "Este mês" e "Mês anterior"',
            'Ver a variação de cada número pelas setas (▲ subiu / ▼ caiu)',
            'Acompanhar admissões, desligamentos, atestados e Primeiros 15',
        ],
    },
    {
        icone: Gift,
        titulo: 'Aniversariantes do Mês',
        imagem: `${BASE_IMG}/aniversariantes.png`,
        texto: 'A lista de quem faz aniversário no mês, para a equipe não deixar passar a data de ninguém. A partir dela você chega ao calendário completo da rede, onde também aparecem os feriados e os eventos cadastrados.',
        acoes: [
            'Ver quem aniversaria no mês',
            'Abrir o calendário completo pelo botão "Ver calendário completo"',
            'Consultar feriados e eventos da rede no calendário',
        ],
    },
    {
        icone: CheckCircle,
        titulo: 'Atividades Pendentes',
        imagem: `${BASE_IMG}/atividades.png`,
        texto: 'A sua lista de tarefas dentro do portal: o que está esperando uma ação sua, de acordo com o seu perfil — por exemplo, pesquisas dos Primeiros 15 dias, triagens de recrutamento ou estornos a confirmar. A bolinha vermelha ao lado de cada item mostra quantas pendências há naquele assunto.',
        acoes: [
            'Ver quantas pendências você tem em cada assunto (bolinha vermelha)',
            'Clicar num item para ir direto à tela onde ele se resolve',
            'Usar como checklist do dia a dia',
        ],
    },
    {
        icone: CalendarCheck,
        titulo: 'Agenda Corporativa (Teams)',
        imagem: `${BASE_IMG}/agenda.png`,
        texto: 'A sua agenda do Teams/Outlook dentro do portal. Depois de conectar a sua conta Microsoft (é só uma vez), os seus compromissos passam a aparecer aqui, e você consegue mexer neles sem sair do sistema. É a sua agenda de verdade — o que você cria aqui aparece no Teams e vice-versa.',
        acoes: [
            'Conectar a sua conta Microsoft (uma vez só)',
            'Ver seus compromissos dos próximos 7 ou 30 dias',
            'Criar, editar e excluir compromissos sem sair do portal',
        ],
    },
    {
        icone: Search,
        titulo: 'Busca e Manual da Tela',
        // Sem print: um screenshot dessa barra não fica bom. Mostra os ícones.
        icones: [Search, HelpCircle],
        texto: 'Na barra do topo ficam dois apoios: a busca geral do sistema (ainda em construção, marcada como "Em breve") e o ícone de interrogação (?), que abre o Manual da Tela. O Manual é uma ajuda rápida sobre a página em que você está no momento — vale a pena abrir sempre que tiver dúvida sobre uma tela.',
        acoes: [
            'Abrir o Manual da Tela pelo ícone de interrogação (?)',
            'Tirar dúvidas sobre a página atual sem sair dela',
        ],
    },
    {
        icone: Bell,
        titulo: 'Sino de Avisos',
        // Sem print: a própria novidade. Mostra o ícone em destaque (amarelo).
        texto: 'É por aqui que você fica sabendo das novidades do portal. Sempre que houver um aviso novo, o sino mostra um número (a quantidade de avisos ainda não lidos). Ao abrir um aviso, ele deixa de contar no número, mas continua na lista em cinza, caso você queira reler depois. Foi clicando no sino que você abriu este tutorial!',
        acoes: [
            'Ver no número quantos avisos novos você tem',
            'Abrir um aviso para ler o conteúdo completo',
            'Reabrir um aviso já lido (fica em cinza na lista)',
        ],
        destaque: true,
    },
];

// ---------------------------------------------------------------------
// Imagem do print com degradê gracioso: se o arquivo ainda não existe na
// pasta pública, mostra uma moldura "imagem em breve" em vez de um ícone
// quebrado. Assim o tutorial já fica pronto antes dos prints chegarem.
// ---------------------------------------------------------------------
function Imagem({ src, alt }) {
    const [erro, setErro] = useState(false);

    if (erro) {
        return (
            <div style={{
                width: '100%', height: '180px', borderRadius: '10px',
                backgroundColor: COR.molduraFundo, border: `1px dashed ${COR.borda}`,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: '8px', color: COR.cinzaSuave,
            }}>
                <ImageIcon size={28} />
                <span style={{ fontSize: '13px', fontWeight: 500 }}>Imagem em breve</span>
            </div>
        );
    }

    return (
        <img
            src={src}
            alt={alt}
            onError={() => setErro(true)}
            style={{
                width: '100%', display: 'block', borderRadius: '10px',
                border: `1px solid ${COR.borda}`,
            }}
        />
    );
}

// ---------------------------------------------------------------------
// Ilustração de ícone — alternativa ao print para seções em que um
// screenshot não fica bom (Sino, Busca/Manual). Mostra o(s) ícone(s)
// grandes sobre um fundo suave, na cor da seção.
// ---------------------------------------------------------------------
function Ilustracao({ icones, cor }) {
    return (
        <div style={{
            width: '100%', height: '150px', borderRadius: '10px',
            backgroundColor: COR.molduraFundo, border: `1px solid ${COR.borda}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '28px',
        }}>
            {icones.map((Ic, i) => (
                <Ic key={i} size={52} color={cor} strokeWidth={1.5} />
            ))}
        </div>
    );
}

function AvisoHomeTour({ onClose }) {
    return (
        // Fundo escurecido — clicar fora fecha.
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 1000, padding: '16px', fontFamily: FONTE,
            }}
        >
            {/* Caixa do pop-up — stopPropagation pra clicar dentro não fechar. */}
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    backgroundColor: '#fff', borderRadius: '14px', width: '100%', maxWidth: '680px',
                    maxHeight: '90vh', display: 'flex', flexDirection: 'column',
                    boxShadow: '0 12px 32px rgba(0,0,0,0.28)', overflow: 'hidden',
                }}
            >
                {/* Cabeçalho */}
                <div style={{
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                    gap: '12px', padding: '22px 24px 18px', borderBottom: `1px solid ${COR.borda}`,
                }}>
                    <div>
                        <div style={{
                            fontSize: '12px', fontWeight: 700, color: COR.amarelo,
                            textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px',
                        }}>
                            Aviso
                        </div>
                        <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 700, color: COR.texto }}>
                            Conheça a tela de início 👋
                        </h2>
                        <p style={{ margin: '6px 0 0', fontSize: '14px', color: COR.cinzaTexto, fontWeight: 400 }}>
                            Veja para que serve cada parte da sua tela inicial e o que você pode fazer em cada uma.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        title="Fechar"
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer', color: COR.cinzaSuave,
                            display: 'flex', padding: '4px', flexShrink: 0,
                        }}
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Corpo rolável: uma seção por parte da Home (print + texto + ações) */}
                <div style={{ overflowY: 'auto', padding: '4px 24px' }}>
                    {SECOES.map((s, i) => {
                        const Icone = s.icone;
                        const corIcone = s.destaque ? COR.amarelo : COR.vermelhoEscuro;
                        return (
                            <div
                                key={i}
                                style={{
                                    padding: '22px 0',
                                    borderBottom: i < SECOES.length - 1 ? `1px solid ${COR.borda}` : 'none',
                                }}
                            >
                                {/* Título com ícone */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                                    <div style={{
                                        width: '38px', height: '38px', borderRadius: '50%', flexShrink: 0,
                                        backgroundColor: COR.fundoIcone, display: 'flex',
                                        alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <Icone size={20} color={corIcone} />
                                    </div>
                                    <div style={{ fontSize: '18px', fontWeight: 700, color: COR.texto }}>
                                        {s.titulo}
                                    </div>
                                </div>

                                {/* Visual da área (acima do texto): print quando há
                                    imagem; senão, ilustração de ícone(s) da seção. */}
                                <div style={{ marginBottom: '12px' }}>
                                    {s.imagem
                                        ? <Imagem src={s.imagem} alt={s.titulo} />
                                        : <Ilustracao icones={s.icones || [s.icone]} cor={corIcone} />}
                                </div>

                                {/* Explicação */}
                                <div style={{ fontSize: '14px', color: COR.cinzaTexto, fontWeight: 400, lineHeight: 1.55 }}>
                                    {s.texto}
                                </div>

                                {/* O que você pode fazer */}
                                {s.acoes && s.acoes.length > 0 && (
                                    <div style={{ marginTop: '12px' }}>
                                        <div style={{ fontSize: '13px', fontWeight: 700, color: COR.texto, marginBottom: '6px' }}>
                                            O que você pode fazer:
                                        </div>
                                        <ul style={{ margin: 0, paddingLeft: '18px', color: COR.cinzaTexto }}>
                                            {s.acoes.map((acao, j) => (
                                                <li key={j} style={{ fontSize: '14px', lineHeight: 1.6 }}>{acao}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Rodapé com o botão de fechar */}
                <div style={{
                    padding: '16px 24px', borderTop: `1px solid ${COR.borda}`,
                    display: 'flex', justifyContent: 'flex-end',
                }}>
                    <button
                        onClick={onClose}
                        style={{
                            backgroundColor: COR.vermelhoEscuro, color: '#fff', border: 'none',
                            borderRadius: '8px', padding: '11px 24px', fontSize: '15px', fontWeight: 600,
                            cursor: 'pointer', fontFamily: FONTE,
                        }}
                    >
                        Entendi
                    </button>
                </div>
            </div>
        </div>
    );
}

export default AvisoHomeTour;
